import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import LegacyDossierRow from "@/components/LegacyDossierRow";
import { api, STAGES, formatEUR } from "@/lib/api";
import { useCaMode } from "@/lib/conversionRates";
import { MagnifyingGlass, X, CaretDown } from "@phosphor-icons/react";

const SORT_OPTIONS = [
  { value: "priority", label: "Priorité" },
  { value: "recent",   label: "Récent" },
  { value: "ca",       label: "CA net" },
  { value: "stars",    label: "Étoiles" },
];

/**
 * Vue commune : segmenté par filterMode (montage | envoyer | banque) ou fallback mandate bool.
 * - montage : mandat non signé
 * - envoyer : mandat signé MAIS date_envoi_banque vide
 * - banque  : date_envoi_banque renseignée (= en banque)
 *
 * V4.48 — Toggle CA strict à 3 modes canoniques (aucun probabiliste ici) :
 *   BRUT    = ca_brut_total (avant Pretto)
 *   PONDÉRÉ = ca_broker     (après Pretto, avant commissions apporteur/parrain)
 *   NET     = ca_net        (après Pretto + commissions)
 * Le CA PRÉVISIONNEL (probabiliste) vit exclusivement dans la page Cash.
 */
export default function DossiersList({ mandate, filterMode, title, subtitle, emptyLabel, embedded = false }) {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("priority");
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [caMode, setCaMode] = useCaMode();
  const searchRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/dossiers");
        if (!cancelled) setAll(res.data || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (searchOpen) setTimeout(() => searchRef.current?.focus(), 50);
  }, [searchOpen]);

  const stageLabel = (key) => STAGES.find((s) => s.key === key)?.label || key || "";
  const q = search.trim().toLowerCase();

  const filtered = useMemo(() => {
    const matchesFilter = (d) => {
      if (filterMode === "montage") return !d.mandat_signe;
      if (filterMode === "envoyer") return d.mandat_signe && !d.date_envoi_banque;
      if (filterMode === "banque") return !!d.date_envoi_banque;
      // fallback legacy
      return !!d.mandat_signe === !!mandate;
    };
    const base = all.filter((d) => d.status === "en_cours" && !d.archived && !d.is_standby && matchesFilter(d));
    const matchSearch = q
      ? base.filter((dos) => {
          const hay = [
            dos.nom, dos.client_name, dos.apporteur, stageLabel(dos.etape), dos.status,
            ...(dos.banques || []),
          ].filter(Boolean).join(" ").toLowerCase();
          return hay.includes(q);
        })
      : base;

    const sorted = [...matchSearch];
    if (sort === "priority") {
      // En montage : ordre strict de maturité commerciale (offre_en_cours → a_appeler).
      // Score = tie-breaker uniquement. Ailleurs : score classique.
      if (filterMode === "montage") {
        sorted.sort((a, b) =>
          (b.maturity_rank || 0) - (a.maturity_rank || 0) ||
          (b.score || 0) - (a.score || 0)
        );
      } else {
        sorted.sort((a, b) => (b.score || 0) - (a.score || 0));
      }
    } else if (sort === "ca") {
      sorted.sort((a, b) => (b.ca_net || 0) - (a.ca_net || 0));
    } else if (sort === "stars") {
      sorted.sort((a, b) => (b.etoiles || 0) - (a.etoiles || 0) || (b.score || 0) - (a.score || 0));
    } else if (sort === "recent") {
      // Plus récent → plus ancien. Ancre = prospect_date (override) || created_at
      const k = (d) => (d.prospect_date || d.created_at || "");
      sorted.sort((a, b) => k(b).localeCompare(k(a)));
    }
    return sorted;
  }, [all, q, sort, mandate, filterMode]);

  // V4.32 — P1.2 : Mini total CA (suit exactement les filtres actifs).
  // V4.48 — 3 modes canoniques stricts (aucun probabiliste) :
  //   Brut    = ca_brut_total (avant Pretto)
  //   Pondéré = ca_broker     (après Pretto, avant commissions apporteur/parrain)
  //   Net     = ca_net        (après Pretto + commissions)
  const totals = useMemo(() => {
    const count = filtered.length;
    const brutOf    = (d) => (d.ca_brut_total ?? d.ca_net ?? 0);
    const pondereOf = (d) => (d.ca_broker      ?? d.ca_net ?? 0);
    const netOf     = (d) => (d.ca_net         ?? 0);
    const ca_brut_total    = filtered.reduce((s, d) => s + brutOf(d), 0);
    const ca_pondere_total = filtered.reduce((s, d) => s + pondereOf(d), 0);
    const ca_net_total     = filtered.reduce((s, d) => s + netOf(d), 0);
    let ca_total;
    if (caMode === "brut")         ca_total = ca_brut_total;
    else if (caMode === "pondere") ca_total = ca_pondere_total;
    else                            ca_total = ca_net_total; // "net"
    const ca_moyen = count > 0 ? ca_total / count : 0;
    return { count, ca_total, ca_moyen, ca_brut_total, ca_pondere_total, ca_net_total };
  }, [filtered, caMode]);

  const updateOne = (updated) => {
    setAll((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
  };

  const closeSearch = () => { setSearch(""); setSearchOpen(false); };

  const content = (
    <>
      {/* Header desktop compact (caché en mode embedded) */}
      {!embedded && (
        <div className="hidden md:block mb-3">
          <h1 className="font-display text-2xl font-extrabold text-slate-900">{title}</h1>
          <div className="text-sm text-slate-500 font-semibold mt-0.5">
            {loading ? "Chargement…" : `${filtered.length} dossier${filtered.length > 1 ? "s" : ""} · ${subtitle}`}
          </div>
        </div>
      )}

      {/* Header mobile compteur */}
      <div className="md:hidden mb-3 text-[12px] text-slate-500 font-semibold" data-testid="dossiers-count">
        {loading ? "Chargement…" : `${filtered.length} dossier${filtered.length > 1 ? "s" : ""}`}
        {embedded && subtitle ? <span className="ml-1 text-slate-400">· {subtitle}</span> : null}
      </div>

      {/* Toolbar : recherche + tri */}
      <div className="mb-3 flex items-center gap-2" data-testid="dossiers-toolbar">
        {!searchOpen ? (
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-300 text-xs font-semibold"
            data-testid="dossiers-search-open"
          >
            <MagnifyingGlass size={14} weight="bold" /> Rechercher
          </button>
        ) : (
          <div className="flex items-center gap-1 flex-1 bg-white border border-[color:var(--cf-primary)] rounded-lg px-2.5 py-1.5 shadow-sm">
            <MagnifyingGlass size={14} weight="bold" color="var(--cf-primary)" />
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Escape") closeSearch(); }}
              placeholder="Nom, client, banque, apporteur, étape…"
              className="flex-1 bg-transparent outline-none text-[13px] text-slate-900 placeholder:text-slate-400 min-w-0"
              data-testid="dossiers-search-input"
            />
            <button
              type="button"
              onClick={closeSearch}
              className="w-6 h-6 rounded flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex-shrink-0"
              data-testid="dossiers-search-close"
            >
              <X size={12} weight="bold" />
            </button>
          </div>
        )}

        {/* Tri */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setSortMenuOpen((v) => !v)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-700 hover:text-slate-900 text-xs font-semibold"
            data-testid="dossiers-sort-toggle"
          >
            Tri : {SORT_OPTIONS.find((o) => o.value === sort)?.label}
            <CaretDown size={11} weight="bold" />
          </button>
          {sortMenuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setSortMenuOpen(false)} />
              <div
                className="absolute right-0 top-full mt-1 z-20 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden w-36"
                data-testid="dossiers-sort-menu"
              >
                {SORT_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => { setSort(opt.value); setSortMenuOpen(false); }}
                    className={`block w-full text-left px-3 py-2 text-xs font-semibold ${
                      sort === opt.value
                        ? "bg-[color:var(--cf-primary-soft)] text-[color:var(--cf-primary)]"
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                    data-testid={`dossiers-sort-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* V4.32 — P1.2 : Mini total CA (suit les filtres actifs : tab + recherche) */}
      {/* V4.48 — Toggle strict 3 modes : Brut / Pondéré / Net (aucun probabiliste ici) */}
      {!loading && totals.count > 0 && (
        <div
          className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2 rounded-lg bg-[color:var(--cf-bg-2)] border border-slate-200 text-[12px]"
          data-testid="dossiers-mini-totaux"
        >
          <span className="font-bold text-slate-900 tabular-nums" data-testid="mini-totaux-count">
            {totals.count} dossier{totals.count > 1 ? "s" : ""}
          </span>
          <span className="text-slate-300">·</span>
          <span className="text-slate-700" data-testid="mini-totaux-ca-total">
            <span className="text-slate-500 mr-1">
              {caMode === "brut" ? "CA brut" : caMode === "pondere" ? "CA pondéré" : "CA net"}
            </span>
            <span className="font-extrabold text-slate-900 tabular-nums">{formatEUR(totals.ca_total)}</span>
          </span>
          <span className="text-slate-300">·</span>
          <span className="text-slate-700" data-testid="mini-totaux-ca-moyen">
            <span className="text-slate-500 mr-1">~</span>
            <span className="font-bold text-slate-900 tabular-nums">{formatEUR(Math.round(totals.ca_moyen))}</span>
            <span className="text-slate-500 ml-1">/ dossier</span>
          </span>

          {/* V4.48 — Toggle canonique 3 modes stricts */}
          <div className="ml-auto flex items-center gap-1.5">
            <div
              className="inline-flex items-center rounded-full bg-white border border-slate-200 p-0.5"
              data-testid="ca-mode-toggle"
              role="tablist"
            >
              <button
                type="button"
                onClick={() => setCaMode("brut")}
                className={`px-2.5 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-wider transition-colors ${
                  caMode === "brut" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-700"
                }`}
                title="CA BRUT : montant total avant rétrocession Pretto (honoraires + com banque + assurance)"
                data-testid="ca-mode-brut-btn"
              >
                Brut
              </button>
              <button
                type="button"
                onClick={() => setCaMode("pondere")}
                className={`px-2.5 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-wider transition-colors ${
                  caMode === "pondere" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-700"
                }`}
                title="CA PONDÉRÉ : montant après rétrocession Pretto (part broker), avant commissions apporteur/parrain"
                data-testid="ca-mode-pondere-btn"
              >
                Pondéré
              </button>
              <button
                type="button"
                onClick={() => setCaMode("net")}
                className={`px-2.5 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-wider transition-colors ${
                  caMode === "net" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-700"
                }`}
                title="CA NET : rémunération réelle du courtier après Pretto + commissions apporteur/parrain"
                data-testid="ca-mode-net-btn"
              >
                Net
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Liste */}
      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-16 rounded-lg bg-white border border-slate-100 animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-6 text-center" data-testid="dossiers-empty">
          <div className="text-sm font-bold text-slate-700">{emptyLabel}</div>
          {q && <div className="text-xs text-slate-500 mt-1">Aucun résultat pour « {search} »</div>}
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="dossiers-list">
          {filtered.map((d) => (
            <LegacyDossierRow
              key={d.id}
              dossier={d}
              onOpen={() => navigate(`/dossier/${d.id}`)}
              onChange={updateOne}
            />
          ))}
        </div>
      )}
    </>
  );

  if (embedded) return content;
  return <Layout title={title} subtitle={subtitle}>{content}</Layout>;
}
